"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Search, Zap, Lock, Smartphone, Globe2, Plane } from "lucide-react";

// market-test-plan.md §3 — guided-hero (search→recommendation) experiment.
const EXPERIMENT_ID = "wf_homepage_help_me_choose_v1";

/**
 * WanderLite hero — friendly-soft pastel sky panel with a CSS landscape band
 * (wander-hills) and a dotted flight path (wander-dash). Big bold navy
 * headline, a rounded-full "I am traveling to" search row that routes to the
 * shared /destinations/ page, and 4 soft trust chips. No glass, no gradients.
 *
 * Analytics: fires the storefront `destination_search_started/submitted` events
 * (market-test §10) carrying the §3 `experiment_id` via the optional field on
 * the shared AnalyticsEvent schema (consent-gated, best-effort).
 */
export default function Hero() {
  const { t } = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");
  const [started, setStarted] = useState(false);

  const onFocus = () => {
    if (started) return;
    setStarted(true);
    track({
      event_name: "destination_search_started",
      event_id: `destination_search_started:${EXPERIMENT_ID}:${Date.now()}`,
      experiment_id: EXPERIMENT_ID,
    });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    track({
      event_name: "destination_search_submitted",
      event_id: `destination_search_submitted:${EXPERIMENT_ID}:${Date.now()}`,
      experiment_id: EXPERIMENT_ID,
    });
    router.push("/destinations/");
  };

  const chips = [
    { icon: Zap, label: t("wander.hero.chip1", "Instant Delivery") },
    { icon: Lock, label: t("wander.hero.chip2", "Secure Payment") },
    { icon: Smartphone, label: t("wander.hero.chip3", "Keep Your Number") },
    { icon: Globe2, label: t("wander.hero.chip4", "No Roaming Fees") },
  ];

  return (
    <section className="wander-sky relative overflow-hidden">
      {/* Friendly backpacker character + CSS landscape band */}
      <div className="wander-hills pointer-events-none absolute inset-x-0 bottom-0 h-44 sm:h-56 z-10" aria-hidden="true" />
      
      {/* Framed traveler character (polaroid style) */}
      <div className="pointer-events-none absolute right-4 bottom-16 sm:right-12 sm:bottom-24 lg:right-24 lg:bottom-12 w-[180px] sm:w-[240px] lg:w-[320px] z-20 rotate-3 animate-float drop-shadow-2xl bg-white p-3 pb-8 sm:p-4 sm:pb-12 rounded-xl border border-slate-100" aria-hidden="true">
        <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-slate-50">
          <ExportedImage
            src="/images/brands/wanderlite-explorer/wander_traveler.webp"
            alt=""
            fill
            priority
            className="object-cover object-center"
          />
        </div>
      </div>
      <div className="pointer-events-none absolute right-12 top-20 hidden items-center gap-2 text-primary/50 lg:flex" aria-hidden="true">
        <span className="wander-dash w-32" />
        <Plane className="h-5 w-5 -rotate-12" />
      </div>

      <div className="container relative mx-auto px-4 pb-20 pt-12 sm:pb-28 lg:pt-20">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-extrabold leading-tight text-foreground sm:text-5xl lg:text-6xl">
            {t("wander.hero.title", "Affordable eSIM data for every adventure.")}
          </h1>

          <p className="mt-5 max-w-xl text-lg text-muted-foreground">
            {t(
              "wander.hero.subtitle",
              "Stay connected anywhere in the world with instant eSIM plans. No SIM card. No roaming fees.",
            )}
          </p>

          {/* Rounded-full search row */}
          <form
            onSubmit={onSubmit}
            className="wander-card mt-8 flex items-center gap-2 rounded-full p-2 pl-5 sm:gap-3"
          >
            <span className="hidden shrink-0 font-semibold text-foreground sm:inline">
              {t("wander.hero.searchLabel", "I am traveling to")}
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={onFocus}
              type="text"
              placeholder={t("wander.hero.searchPlaceholder", "Select Destination")}
              aria-label={t("wander.hero.searchAria", "I am traveling to — select destination")}
              className="min-w-0 flex-1 bg-transparent text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
            <button
              type="submit"
              aria-label={t("wander.hero.searchCta", "Search destinations")}
              className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition-colors hover:bg-primary/90"
            >
              <Search className="h-5 w-5" />
            </button>
          </form>

          {/* Trust chips */}
          <ul className="mt-7 flex flex-wrap gap-x-7 gap-y-3">
            {chips.map(({ icon: Icon, label }) => (
              <li key={label} className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80">
                <span className="wander-chip inline-flex h-9 w-9 items-center justify-center">
                  <Icon className="h-4 w-4" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
